import React from 'react';
import { Spin } from 'antd';
import type { SpinProps } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * LoadingSpinner Component
 *
 * A wrapper around Ant Design's Spin component for consistent loading states
 * throughout the application, especially during async game operations.
 */

export interface LoadingSpinnerProps extends Omit<SpinProps, 'size'> {
  /**
   * The size of the spinner
   * @default 'default'
   */
  size?: 'small' | 'default' | 'large';
  /**
   * Custom text or tip to display below the spinner
   */
  tip?: string;
  /**
   * Whether to show the spinner in a fullscreen overlay
   * @default false
   */
  fullscreen?: boolean;
  /**
   * Delay in milliseconds before showing the spinner
   * Useful for preventing flicker on fast operations
   * @default 200
   */
  delay?: number;
  /**
   * Custom className for additional styling
   */
  className?: string;
}

/**
 * Standard loading spinner for inline use
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'default',
  tip,
  fullscreen = false,
  delay = 200,
  className = '',
  ...props
}) => {
  const spinProps: SpinProps = {
    size,
    tip,
    delay,
    ...props,
  };

  if (fullscreen) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-50 ${className}`}>
        <Spin {...spinProps} />
      </div>
    );
  }

  return <Spin className={className} {...spinProps} />;
};

/**
 * Game loading spinner with default game-specific tip
 */
export const GameLoadingSpinner: React.FC<Omit<LoadingSpinnerProps, 'tip'>> = (props) => {
  const { t } = useTranslation();
  return <LoadingSpinner tip={t('game.spinner.loadingGame')} size="large" {...props} />;
};

/**
 * Connection spinner for WebSocket reconnection
 */
export const ConnectionSpinner: React.FC<Omit<LoadingSpinnerProps, 'tip'>> = (props) => {
  const { t } = useTranslation();
  return <LoadingSpinner tip={t('game.spinner.connecting')} size="small" {...props} />;
};

/**
 * Action processing spinner for game actions (move, fire, rally)
 */
export const ActionProcessingSpinner: React.FC<Omit<LoadingSpinnerProps, 'tip'>> = (props) => {
  const { t } = useTranslation();
  return <LoadingSpinner tip={t('game.spinner.processingAction')} delay={100} {...props} />;
};

export default LoadingSpinner;
